import React, { useState } from 'react';
import { 
  MessageCircle, 
  Instagram, 
  MessageSquare, 
  Users, 
  BarChart3, 
  Edit3, 
  Target, 
  Palette,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ContactModal from '../components/ContactModal';
import { trackButtonClick } from '../utils/analytics';
import mockData from '../mock';

const Services = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Icon mapping
  const iconMap = {
    MessageCircle,
    Instagram,
    MessageSquare,
    Users,
    BarChart3,
    Edit3,
    Target,
    Palette
  };

  const handleGetStrategyClick = () => {
    trackButtonClick('Get Strategy', 'services_page');
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-page)' }}>
      {/* Header Section */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="heading-1 mb-6 fade-in" style={{ color: 'var(--text-primary)' }}>Our Services</h1>
            <p className="body-large max-w-3xl mx-auto fade-in fade-in-delay-1" style={{ color: 'var(--text-secondary)' }}>
              Comprehensive digital marketing solutions designed to deliver measurable results. 
              Each service is tailored to your specific business needs and objectives.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mockData.services.detailed.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Target;
              
              return (
                <div 
                  key={service.id} 
                  className={`service-card fade-in fade-in-delay-${Math.min((index % 3) + 1, 3)} p-8 rounded-2xl border`}
                  style={{ 
                    backgroundColor: 'var(--bg-card)',
                    borderColor: 'var(--border-medium)'
                  }}
                >
                  <div className="flex items-center mb-6">
                    <div className="p-3 rounded-full mr-4" style={{ backgroundColor: 'rgba(217, 251, 6, 0.1)' }}>
                      <IconComponent className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
                    </div>
                    <h3 className="heading-3" style={{ color: 'var(--text-primary)' }}>
                      {service.title}
                    </h3>
                  </div>
                  
                  <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                  
                  <div className="flex items-start rounded-lg p-4 border" style={{ 
                    backgroundColor: 'rgba(217, 251, 6, 0.05)',
                    borderColor: 'rgba(217, 251, 6, 0.2)'
                  }}>
                    <Sparkles className="h-4 w-4 mr-2 mt-1 flex-shrink-0" style={{ color: 'var(--brand-primary)' }} />
                    <p className="body-small font-semibold" style={{ color: 'var(--brand-primary)' }}>
                      {service.highlight}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20" style={{ backgroundColor: 'var(--bg-card)' }}>
        <div className="container max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="heading-2 mb-6 fade-in" style={{ color: 'var(--text-primary)' }}>Our Process</h2>
            <p className="body-large max-w-2xl mx-auto fade-in fade-in-delay-1" style={{ color: 'var(--text-secondary)' }}>
              A structured approach that ensures every campaign delivers maximum ROI
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Strategy Development',
                description: 'We analyze your business, audience, and competition to create a tailored strategy.'
              },
              {
                step: '02',
                title: 'Campaign Setup',
                description: 'Launch optimized campaigns across the most effective channels for your niche.'
              },
              {
                step: '03',
                title: 'Optimization',
                description: 'Continuous testing and optimization to improve performance and reduce costs.'
              },
              {
                step: '04',
                title: 'Reporting',
                description: 'Transparent reporting with actionable insights and clear ROI metrics.'
              }
            ].map((item, index) => (
              <div key={index} className={`text-center fade-in fade-in-delay-${index + 1}`}>
                <div className="mb-6">
                  <span className="text-6xl font-bold" style={{ color: 'rgba(217, 251, 6, 0.2)' }}>
                    {item.step}
                  </span>
                </div>
                <h3 className="heading-4 mb-4" style={{ color: 'var(--text-primary)' }}>{item.title}</h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto px-6">
          <div className="text-center">
            <div 
              className="service-card max-w-4xl mx-auto fade-in p-16 rounded-2xl border"
              style={{ 
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-medium)'
              }}
            >
              <h2 className="heading-2 mb-6" style={{ color: 'var(--text-primary)' }}>Ready to Get Started?</h2>
              <p className="body-large mb-8 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Let's discuss how our services can help your business achieve its advertising goals. 
                Get a free consultation and personalized strategy.
              </p>
              <button 
                className="btn-primary px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:scale-105"
                style={{ 
                  backgroundColor: 'var(--brand-primary)',
                  color: 'var(--text-inverse)'
                }}
                onClick={handleGetStrategyClick}
              >
                Get Your Free Strategy
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title="Get Your Free Strategy"
      />
    </div>
  );
};

export default Services;